import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { 
  ChevronUp, 
  ChevronDown,
  Heart, 
  MessageCircle, 
  Share2, 
  Bookmark, 
  MapPin, 
  Clock,
  X,
  MoreHorizontal,
  Send 
} from "lucide-react";
import AudioWaveform from "../Carousel/Carousel";
import "./verticalScroll.css";

const VerticalScrollDisplay = ({ medias, postData, onAddComment }) => {
  const [selectedMedia, setSelectedMedia] = useState(medias[0] || null);
  const [detailMedia, setDetailMedia] = useState(null);
  const [commentText, setCommentText] = useState("");
  const scrollContainerRef = useRef(null);

  const handleMediaClick = (media, event) => {
    event.stopPropagation(); // ✅ PREVENT POPUP
    setSelectedMedia(media);
  };

  const handleMediaExpand = (media, event) => {
    event.stopPropagation(); // ✅ PREVENT POPUP
    setDetailMedia(media);
  };

  const handleDetailClose = (event) => {
    event.stopPropagation();
    setDetailMedia(null);
  };

  const scroll = (direction, event) => {
    event.stopPropagation(); // ✅ PREVENT POPUP
    const container = scrollContainerRef.current;
    if (!container) return;

    const thumbnailHeight = container.querySelector('.vs-thumbnail-item')?.offsetHeight || 150;
    const gap = 12;
    const scrollAmount = thumbnailHeight + gap;

    container.scrollBy({
      top: direction === "down" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  const handleContainerClick = (event) => {
    event.stopPropagation(); // ✅ PREVENT POPUP
  };

  const handleCommentSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (commentText.trim() && onAddComment) {
      onAddComment(postData.postId, commentText);
      setCommentText("");
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Just now";
    const now = new Date();
    const posted = new Date(timestamp);
    const diff = Math.floor((now - posted) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Render primary media
  const renderPrimaryMedia = (media) => {
    if (!media) return null;

    switch (media.mediatype) {
      case "image":
        return (
          <img
            src={media.mediaurl}
            alt={media.alt || "Primary media"}
            className="vs-primary-media"
            onClick={(e) => handleMediaExpand(media, e)}
          />
        );
      case "video":
        return (
          <video
            src={media.mediaurl}
            className="vs-primary-media"
            controls
            playsInline
            onClick={(e) => handleMediaExpand(media, e)}
          />
        );
      case "audio":
        return (
          <div className="vs-primary-audio" onClick={(e) => handleMediaExpand(media, e)}>
            <AudioWaveform src={media.mediaurl} />
            <audio src={media.mediaurl} controls />
          </div>
        );
      default:
        return null;
    }
  };

  const renderThumbnailMedia = (media, index) => {
    switch (media.mediatype) {
      case "image":
        return (
          <img
            src={media.mediaurl}
            alt={`Thumbnail ${index + 1}`}
            className="vs-thumbnail-media"
          />
        );
      case "video":
        return (
          <div className="vs-thumbnail-video-wrapper">
            <video
              src={media.mediaurl}
              className="vs-thumbnail-media"
              muted
            />
            <div className="vs-video-play-icon">▶</div>
          </div>
        );
      case "audio":
        return (
          <div className="vs-thumbnail-audio">
            <div className="vs-audio-icon">🎵</div>
            <span className="vs-audio-label">Audio</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Render detail popup media
  const renderDetailMedia = (media) => {
    if (!media) return null;

    switch (media.mediatype) {
      case "image":
        return (
          <img
            src={media.mediaurl}
            alt={media.alt || "Media"}
            className="vs-detail-media-content"
          />
        );
      case "video":
        return (
          <video
            src={media.mediaurl}
            className="vs-detail-media-content"
            controls
            autoPlay
          />
        );
      case "audio":
        return (
          <div className="vs-detail-audio-wrapper">
            <AudioWaveform src={media.mediaurl} />
            <audio src={media.mediaurl} controls autoPlay />
          </div>
        );
      default:
        return null;
    }
  };

  // Calculate number of visible thumbnails (2-3)
  const visibleThumbnails = medias.length === 1 ? 1 : Math.min(medias.length, 3);

  return (
    <>
      <div className="vs-container" onClick={handleContainerClick}>
        {/* Primary Media Section - Dynamic Width */}
        <div className="vs-primary-section" onClick={handleContainerClick}>
          <div className="vs-primary-content">
            {renderPrimaryMedia(selectedMedia)}
          </div>
        </div>

        {/* Secondary Thumbnails Section - Fixed Width */}
        <div className="vs-secondary-section" onClick={handleContainerClick}>
          {/* Up Scroll Button - Show only if more than visible */}
          {medias.length > visibleThumbnails && (
            <button
              className="vs-scroll-button vs-scroll-up"
              onClick={(e) => scroll("up", e)}
              aria-label="Scroll up"
            >
              <ChevronUp className="vs-chevron-icon" />
            </button>
          )}

          {/* Thumbnails Container */}
          <div
            className={`vs-thumbnails-wrapper vs-thumbnails-${visibleThumbnails}`}
            ref={scrollContainerRef}
            onClick={handleContainerClick}
          >
            {medias.map((media, index) => (
              <div
                key={index}
                className={`vs-thumbnail-item ${
                  selectedMedia?.mediaurl === media.mediaurl ? 'vs-thumbnail-selected' : ''
                }`}
                onClick={(e) => handleMediaClick(media, e)}
              >
                {renderThumbnailMedia(media, index)}
              </div>
            ))}
          </div>

          {/* Down Scroll Button - Show only if more than visible */}
          {medias.length > visibleThumbnails && (
            <button
              className="vs-scroll-button vs-scroll-down"
              onClick={(e) => scroll("down", e)}
              aria-label="Scroll down"
            >
              <ChevronDown className="vs-chevron-icon" />
            </button>
          )}
        </div>
      </div>

      {/* Detail Popup */}
      {detailMedia && (
        <DetailPopup
          media={detailMedia}
          postData={postData}
          onClose={handleDetailClose}
          renderMedia={renderDetailMedia}
          commentText={commentText}
          setCommentText={setCommentText}
          handleCommentSubmit={handleCommentSubmit}
          getTimeAgo={getTimeAgo}
        />
      )}
    </>
  );
};

// Detail Popup Component
const DetailPopup = ({
  media,
  postData,
  onClose,
  renderMedia,
  commentText,
  setCommentText,
  handleCommentSubmit,
  getTimeAgo,
}) => {
  return (
    <div className="vs-detail-overlay" onClick={onClose}>
      <div className="vs-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="vs-detail-close" onClick={onClose} aria-label="Close">
          <X size={24} />
        </button>

        {/* Left Side - Media */}
        <div className="vs-detail-media">
          {renderMedia(media)}
        </div>

        {/* Right Side - Post Details */}
        <div className="vs-detail-sidebar" style={{width: 300, height: "100%"}}>
          {/* Header */}
          <div className="vs-detail-header" style={{fontSize: 18}}>
            <div className="vs-detail-user">
              <img
                src={postData?.profileImage || "/default-avatar.png"}
                alt={postData?.username || "User"}
                className="vs-detail-avatar"
              />
              <div className="vs-detail-user-info">
                <div className="vs-detail-username">
                  {postData?.username || "Anonymous"}
                </div>
                {postData?.location && (
                  <div className="vs-detail-location">
                    <MapPin size={12} />
                    <span>{postData.location}</span>
                  </div>
                )}
              </div>
            </div>
            <button className="vs-detail-menu">
              <MoreHorizontal size={20} />
            </button>
          </div>

          {/* Description */}
          {postData?.descriptions && (
            <div className="vs-detail-description">
              <span className="vs-detail-desc-username">{postData.username}</span>
              <span className="vs-detail-desc-text">{postData.descriptions}</span>
            </div>
          )}

          {/* Comments Section */}
          <div className="vs-detail-comments">
            {postData?.comments && postData.comments.length > 0 ? (
              postData.comments.map((comment, index) => (
                <div key={index} className="vs-detail-comment">
                  <img
                    src={comment.userImage || "/default-avatar.png"}
                    alt={comment.username}
                    className="vs-detail-comment-avatar"
                  />
                  <div className="vs-detail-comment-content">
                    <div className="vs-detail-comment-text">
                      <span className="vs-detail-comment-username">
                        {comment.username}
                      </span>
                      <span>{comment.text}</span>
                    </div>
                    <div className="vs-detail-comment-meta">
                      <span>{getTimeAgo(comment.timestamp)}</span>
                      {comment.likes > 0 && <span>{comment.likes} likes</span>}
                      <button>Reply</button>
                    </div>
                  </div>
                  <button className="vs-detail-comment-like">
                    <Heart size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="vs-detail-no-comments">
                <MessageCircle size={48} />
                <p>No comments yet</p>
                <span>Start the conversation</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="vs-detail-footer">
            <div className="vs-detail-actions">
              <div className="vs-detail-actions-left">
                <button className="vs-detail-action-btn" aria-label="Like">
                  <Heart size={24} />
                </button>
                <button className="vs-detail-action-btn" aria-label="Comment">
                  <MessageCircle size={24} />
                </button>
                <button className="vs-detail-action-btn" aria-label="Share">
                  <Share2 size={24} />
                </button>
              </div>
              <button className="vs-detail-action-btn" aria-label="Save">
                <Bookmark size={24} />
              </button>
            </div>

            <div className="vs-detail-stats">
              <div className="vs-detail-likes">
                {postData?.likes || 0} likes
              </div>
              <div className="vs-detail-time">
                <Clock size={12} />
                <span>{getTimeAgo(postData?.timestamp)}</span>
              </div>
            </div>

            {postData?.impressions && (
              <div className="vs-detail-impressions">
                👁️ {postData.impressions.toLocaleString()} impressions
              </div>
            )}

            <form className="vs-detail-comment-form" onSubmit={handleCommentSubmit}>
              <input
                type="text"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="vs-detail-comment-input"
              />
              <button
                type="submit"
                disabled={!commentText.trim()}
                className="vs-detail-comment-submit"
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

VerticalScrollDisplay.propTypes = {
  medias: PropTypes.arrayOf(
    PropTypes.shape({
      mediatype: PropTypes.oneOf(["image", "audio", "video"]).isRequired,
      mediaurl: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ).isRequired,
  postData: PropTypes.shape({
    postId: PropTypes.string,
    username: PropTypes.string,
    profileImage: PropTypes.string,
    location: PropTypes.string,
    descriptions: PropTypes.string,
    timestamp: PropTypes.string,
    likes: PropTypes.number,
    comments: PropTypes.array,
    impressions: PropTypes.number,
  }),
  onAddComment: PropTypes.func,
};

export default VerticalScrollDisplay;
